// src/App.js

import React from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/toast.css";
import "./App.css";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { routesConfig } from "./Routes/routesConfig";
import ScrollToTop from "./components/ScrollToTop";
import { SITE_CONFIG, getPageMetadata, getFullUrl } from "./utils/metadata";
import useSessionReconciliation from "./hooks/useSessionReconciliation";

// Component to handle route-specific metadata
const RouteMetadata = ({ route }) => {
  const location = useLocation();

  // Skip if route doesn't have title (some routes might not need metadata)
  if (!route.title) {
    return null;
  }

  // Use actual pathname for routes with parameters, otherwise use route path
  // For external URLs, use the route path as-is
  let path = location.pathname;
  if (route.path && route.path.startsWith("http")) {
    // External URL - use as-is
    path = route.path;
  } else if (route.path && !route.path.includes(":")) {
    // Static route - use route path
    path = route.path;
  }
  // For dynamic routes, pathname is already set correctly

  const metadata = getPageMetadata({
    title: route.title,
    description: route.description || SITE_CONFIG.description,
    path: path,
    image: route.image,
    type: route.metaType || "website",
    noindex: route.noindex || false,
  });

  return (
    <Helmet defer={false}>
      {/* Primary Meta Tags */}
      <title>{metadata.title}</title>
      <meta name="title" content={metadata.title} />
      <meta name="description" content={metadata.description} />
      {metadata.noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={metadata.type} />
      <meta property="og:url" content={metadata.url} />
      <meta property="og:title" content={metadata.title} />
      <meta property="og:description" content={metadata.description} />
      <meta property="og:image" content={metadata.image} />
      <meta property="og:site_name" content={SITE_CONFIG.name} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={metadata.url} />
      <meta name="twitter:title" content={metadata.title} />
      <meta name="twitter:description" content={metadata.description} />
      <meta name="twitter:image" content={metadata.image} />
      {SITE_CONFIG.twitterHandle && (
        <meta name="twitter:creator" content={SITE_CONFIG.twitterHandle} />
      )}

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#00B894" />
      <link rel="canonical" href={metadata.url} />
    </Helmet>
  );
};

function App() {
  // A stale sign-in marker used to survive until something happened to make a
  // protected request. Someone arriving from the marketing site to run an
  // eligibility check might never make one, so the header claimed they were
  // signed in while the session had long since expired. One cheap call at boot
  // settles it.
  useSessionReconciliation();

  return (
    <div className="flex flex-col min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <main className="flex-1">
          <Routes>
            {routesConfig.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={
                  <>
                    <RouteMetadata route={route} />
                    {route.element}
                  </>
                }
              />
            ))}
          </Routes>
        </main>
        <Footer />
        {/* Global toast notifications used by the application form submit
            flow, Profile/ChangePassword, and useLogout. Single mount point so
            every call to toast(...) actually surfaces a notification. Brand
            colours and typography are applied via src/styles/toast.css. */}
        <ToastContainer
          position="top-right"
          autoClose={4000}
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          theme="light"
        />
      </BrowserRouter>
    </div>
  );
}

export default App;